import React, { useState } from "react"
import { getCountryName } from "helpers/countries"
import dayjs from "dayjs"
import DineroFactory from "dinero.js"
import {
  CustomReasonCode,
  Order,
  OrderGiftCardProductRetain24,
  Reason
} from "lib/types/generated/graphql-types"
import { useMutation, useQuery } from "@apollo/client"

import GET_STORE_GROUP from "../../../../../graphql/queries/store/GetStoreGroup"

import { Header } from "../Shared.styled"
import { ButtonWrapper, Container, Summary } from "./OrderSummary.styled"

import Money from "../../../../Money"
import { Item, Label, Value } from "../../../../Ui/List/List"
import FlagIcon from "../../../../Ui/FlagIcon"
import DotLoader from "../../../../Ui/DotLoader"
import CopyButton from "../../../../Ui/CopyButton"
import CREATE_DELIVERY from "../../../../../graphql/mutations/order/deliveries/CreateDelivery"
import PrimaryButton from "../../../../Ui/Buttons/PrimaryButton"
import { VariantRow } from "./VariantRow"
import { GiftRow } from "./GiftRow"
import { ShippingRow } from "./ShippingRow"
import { handleErrorMessages } from "helpers/errors"
import { GiftCardProductRow } from "./GiftCardProductRow"
import { Delivery } from "../../../../../classes/order/Delivery"
import { OrderLoadingSkeleton } from "./OrderLoadingSkeleton"
import { isOrderGiftCardProductRetain24, isSkipReason } from "../../../helpers"
import ReasonInput from "../../Reason/ReasonInput"
import { ReasonWrapper } from "../../DeliverOrder/OrderSummary/OrderSummary.styled"
import { ReasonType } from "lib/types/order"

type Props = {
  order: Order
  refetch: () => void
  orderLoading: boolean
  customReasons: CustomReasonCode[]
}

export const OrderSummary = ({ order, refetch, orderLoading, customReasons }: Props) => {
  const [reason, setReason] = useState<Reason>({ code: "", cause: "" })
  const [reasonType, setReasonType] = useState<ReasonType>()
  const { data, loading } = useQuery(GET_STORE_GROUP, {
    fetchPolicy: "cache-first",
    variables: { id: order?.storeGroupId },
    skip: !order?.storeGroupId
  })

  const deliveries = new Delivery(order?.Deliveries)

  const [createDelivery, { loading: createLoading }] = useMutation(CREATE_DELIVERY, {
    onCompleted: () => {
      setReason({ cause: "", code: "" })
      setReasonType(undefined)
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const remainingOrderLines = deliveries.getRemainingOrderLines(order?.orderLines ?? [])
  const remainingShippingFees = deliveries.getRemainingShippingFees(order?.shippingFees ?? [])
  const remainingGiftCardProducts = order?.GiftCardProduct
    ? deliveries.getRemainingGiftCardProducts(order.GiftCardProduct)
    : undefined
  const remainingGifts = order?.gifts ? deliveries.getRemainingGifts(order?.gifts) : []

  const createOrderDelivery = () => {
    return createDelivery({
      variables: {
        orderId: order.id,
        input: {
          orderLines: remainingOrderLines?.map((orderLine) => ({
            orderLineId: orderLine.id,
            quantity: orderLine.quantity
          })),
          ...(reason.cause && reason.code && !isSkipReason(reasonType) && { reason }),
          ...(remainingGifts &&
            remainingGifts.length > 0 && {
              gifts: remainingGifts.map((gift) => ({ giftId: gift.id, quantity: gift.quantity }))
            }),
          shippingFees: remainingShippingFees?.map((fee) => fee.id),
          ...(isOrderGiftCardProductRetain24(order?.GiftCardProduct) &&
            (remainingGiftCardProducts as OrderGiftCardProductRetain24).giftCardProducts && {
              giftCardProducts: (
                remainingGiftCardProducts as OrderGiftCardProductRetain24
              ).giftCardProducts?.map((giftCardProduct) => ({
                giftCardProductId: giftCardProduct.id
              }))
            })
        }
      }
    })
  }

  const orderLinesWithoutCompletedDeliveries = order?.orderLines?.filter((ol) =>
    order.Deliveries.map((delivery) => delivery.orderLines.map((dol) => dol.orderLineId !== ol.id))
  )

  const shippingFeesWithoutCompletedDeliveries = order?.shippingFees?.filter((orderFee) =>
    order.Deliveries.map((delivery) =>
      delivery.shippingFees.map((deliveryFee) => orderFee.id !== deliveryFee.shippingFeeId)
    )
  )

  const giftCardsWithoutCompletedDeliveries = isOrderGiftCardProductRetain24(order?.GiftCardProduct)
    ? order?.GiftCardProduct?.giftCardProducts?.filter((orderFee) =>
        order.Deliveries.map((delivery) =>
          delivery.shippingFees.map((deliveryFee) => orderFee.id !== deliveryFee.shippingFeeId)
        )
      )
    : []

  const giftsWithoutCompletedDeliveries = order?.gifts?.filter((gift) =>
    order.Deliveries.map((delivery) =>
      delivery.gifts.map((deliveryGift) => deliveryGift.giftId !== gift.id)
    )
  )

  const createButtonDisabled = (!reason.cause || !reason.code) && reasonType !== ReasonType.SKIP

  if (orderLoading) return <OrderLoadingSkeleton />
  if (!order) return null
  return (
    <Container>
      <Header>
        <h2>Order: #{order?.reference}</h2>
      </Header>
      <Summary>
        <Item flexibleHeight>
          <Label>E-mail:</Label>
          <Value>
            <>
              {order?.billingAddress?.email}
              <CopyButton string={order?.billingAddress?.email} />
            </>
          </Value>
        </Item>
        <Item>
          <Label>Store group:</Label>
          <Value>{loading ? <DotLoader /> : data?.getStoreGroup?.name}</Value>
        </Item>
        <Item>
          <Label>Store market:</Label>
          <Value flex>
            <>
              <FlagIcon countryCode={order?.countryCode} />
              {getCountryName(order?.countryCode)}
            </>
          </Value>
        </Item>
        <Item>
          <Label>Order date:</Label>
          <Value>{dayjs(order?.date).format("YYYY-MM-DD HH:mm")}</Value>
        </Item>
        <Item>
          <Label>Total order value:</Label>
          <Value>
            <Money
              amount={parseInt(`${order?.totals?.grandTotal ?? 0}`)}
              currencyUnit={order?.currencyCode as DineroFactory.Currency}
            />
          </Value>
        </Item>
        {!deliveries.onGoing() && (
          <ReasonWrapper>
            <b>Reason:</b>
            <ReasonInput
              setReason={setReason}
              reason={reason}
              customReasons={customReasons}
              reasonType={reasonType}
              setReasonType={setReasonType}
            />
          </ReasonWrapper>
        )}
        {!deliveries.onGoing() && (
          <ButtonWrapper>
            <PrimaryButton
              handleClick={createOrderDelivery}
              loading={createLoading}
              disabled={createButtonDisabled}
            >
              Create delivery draft
            </PrimaryButton>
          </ButtonWrapper>
        )}
      </Summary>
      {deliveries.onGoing() && (
        <>
          <h2>Order lines ({orderLinesWithoutCompletedDeliveries?.length ?? 0})</h2>
          {orderLinesWithoutCompletedDeliveries?.map((orderLine) => (
            <VariantRow
              key={orderLine?.id}
              orderLine={orderLine}
              order={order}
              refetch={refetch}
              currencyCode={order?.currencyCode}
            />
          ))}
          {giftsWithoutCompletedDeliveries && giftsWithoutCompletedDeliveries?.length > 0 && (
            <>
              <h2>Order gifts ({giftsWithoutCompletedDeliveries?.length ?? 0})</h2>
              {giftsWithoutCompletedDeliveries?.map((gift) => (
                <GiftRow
                  key={gift?.id}
                  gift={gift}
                  order={order}
                  refetch={refetch}
                  currencyCode={order?.currencyCode}
                />
              ))}
            </>
          )}
          {shippingFeesWithoutCompletedDeliveries &&
            shippingFeesWithoutCompletedDeliveries.length > 0 && (
              <>
                <h2>Shipping ({shippingFeesWithoutCompletedDeliveries?.length ?? 0})</h2>
                {shippingFeesWithoutCompletedDeliveries?.map((shippingFee) => (
                  <ShippingRow
                    key={shippingFee?.id}
                    shippingFee={shippingFee}
                    order={order}
                    refetch={refetch}
                    currencyCode={order?.currencyCode}
                  />
                ))}
              </>
            )}
          {(giftCardsWithoutCompletedDeliveries?.length ?? 0) ? (
            <>
              <h2>Gift card({giftCardsWithoutCompletedDeliveries?.length ?? 0})</h2>
              {giftCardsWithoutCompletedDeliveries?.map((giftCardProduct) => (
                <GiftCardProductRow
                  key={giftCardProduct?.id}
                  giftCardProduct={giftCardProduct}
                  order={order}
                  refetch={refetch}
                  currencyCode={order?.currencyCode}
                />
              ))}
            </>
          ) : (
            <></>
          )}
        </>
      )}
    </Container>
  )
}
